import React, { useEffect, useRef, useState } from 'react';
import { useLang } from './LangContext';

/**
 * AvatarCropper — lightweight canvas-based circular crop modal.
 * Props:
 *   file      : File (required)
 *   outputSize: number (px, final square, default 512)
 *   onCancel  : () => void
 *   onConfirm : (blob, previewUrl) => Promise<void> | void
 *
 * Interaction:
 *   - drag = pan image
 *   - wheel / slider = zoom
 */
const AvatarCropper = ({ file, outputSize = 512, onCancel, onConfirm }) => {
  const lang = useLang();
  const viewSize = 320; // px (square preview viewport)
  const [img, setImg] = useState(null); // HTMLImageElement
  const [zoom, setZoom] = useState(1);    // multiplier of "cover" scale
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // px offset from center
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const viewportRef = useRef(null);
  // BUG FIX: previously the object-URL was revoked inside `image.onload` —
  // but we continue to read `img.src` afterwards to drive
  // `backgroundImage: url(...)` on the preview layer. The browser then tries
  // to fetch a blob URL that has already been invalidated, which renders as
  // a black square. Keep the URL alive for the whole lifetime of this
  // component and revoke only on unmount.
  const objectUrlRef = useRef(null);

  // Load the picked file as an image
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    const image = new Image();
    image.onload = () => { setImg(image); };
    image.onerror = () => { setError(lang.AVATAR_READ_ERROR || 'Không đọc được ảnh.'); };
    image.src = url;
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // BUG FIX: lock background page scroll for the entire lifetime of the
  // modal. Previously only the viewport had `onWheel={preventDefault}`,
  // which React attaches as a *passive* listener — so the call is silently
  // ignored and the page scrolls together with the zoom. Locking body
  // overflow side-steps this entirely while we're in crop mode.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Bind a *non-passive* wheel listener directly on the viewport DOM node so
  // we can actually `preventDefault()` and stop the scroll from also
  // bubbling up to the page (React's synthetic `onWheel` is passive).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const delta = -e.deltaY / 500;
      setZoom(z => Math.max(1, Math.min(4, z + delta)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [img]);

  // Minimum scale to cover the viewport square
  const coverScale = img ? Math.max(viewSize / img.width, viewSize / img.height) : 1;
  const scale = coverScale * zoom;

  // Clamp offset so the image fully covers the viewport circle
  const clampOffset = (o) => {
    if (!img) return o;
    const drawnW = img.width * scale;
    const drawnH = img.height * scale;
    const maxX = Math.max(0, (drawnW - viewSize) / 2);
    const maxY = Math.max(0, (drawnH - viewSize) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, o.x)),
      y: Math.max(-maxY, Math.min(maxY, o.y))
    };
  };

  useEffect(() => { setOffset(o => clampOffset(o)); // eslint-disable-next-line
  }, [zoom, img]);

  const onPointerDown = (e) => {
    setDragging(true);
    const pt = e.touches ? e.touches[0] : e;
    dragStart.current = { x: pt.clientX, y: pt.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragging || !dragStart.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const dx = pt.clientX - dragStart.current.x;
    const dy = pt.clientY - dragStart.current.y;
    // Prevent page from scrolling while the user is dragging inside the viewport.
    if (e.touches && e.cancelable) e.preventDefault();
    setOffset(clampOffset({ x: dragStart.current.ox + dx, y: dragStart.current.oy + dy }));
  };
  const endDrag = () => { setDragging(false); dragStart.current = null; };

  const onWheel = (e) => {
    // Kept only as a fallback for environments where the ref-attached
    // non-passive listener does not bind (very old browsers). The real
    // zoom is driven by the non-passive listener registered in useEffect.
    const delta = -e.deltaY / 500;
    setZoom(z => Math.max(1, Math.min(4, z + delta)));
  };

  const confirm = async () => {
    if (!img) return;
    setWorking(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');
      // Viewport -> source mapping: each pixel of viewSize covers (1/scale) source pixels.
      // srcRect top-left in source image coordinates.
      const srcW = viewSize / scale;
      const srcH = viewSize / scale;
      const srcX = (img.width / 2) - (offset.x / scale) - (srcW / 2);
      const srcY = (img.height / 2) - (offset.y / scale) - (srcH / 2);
      // Circular clip
      ctx.save();
      ctx.beginPath();
      ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputSize, outputSize);
      ctx.restore();
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png', 0.92));
      if (!blob) throw new Error(lang.AVATAR_CROP_ERROR || 'Không tạo được ảnh cắt.');
      const previewUrl = canvas.toDataURL('image/png');
      await onConfirm(blob, previewUrl);
    } catch (err) {
      setError(err.message);
      setWorking(false);
    }
  };

  return (
    <div
      className="avatar-crop-backdrop"
      onMouseUp={endDrag}
      onMouseMove={onPointerMove}
      onTouchEnd={endDrag}
      onTouchCancel={endDrag}
      onTouchMove={onPointerMove}
    >
      <div className="avatar-crop-modal" onClick={e => e.stopPropagation()}>
        <h3 className="avatar-crop-title">{lang.AVATAR_CROP_TITLE || 'Cắt ảnh đại diện'}</h3>
        <p className="avatar-crop-hint">{lang.AVATAR_CROP_HINT || 'Kéo để di chuyển, cuộn chuột hoặc kéo thanh để thu phóng.'}</p>
        {error && <div className="avatar-crop-error">{error}</div>}

        <div
          ref={viewportRef}
          className="avatar-crop-viewport"
          style={{ width: viewSize, height: viewSize, touchAction: 'none' }}
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          onWheel={onWheel}
        >
          {img && (
            <div
              className="avatar-crop-image-layer"
              style={{
                width: img.width * scale,
                height: img.height * scale,
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                backgroundImage: `url(${img.src})`
              }}
            />
          )}
          <div className="avatar-crop-mask" />
        </div>

        <div className="avatar-crop-zoom">
          <span>{lang.AVATAR_ZOOM || 'Thu phóng'}</span>
          <input
            type="range"
            min="1"
            max="4"
            step="0.01"
            value={zoom}
            onChange={e => setZoom(parseFloat(e.target.value))}
          />
          <span className="avatar-crop-zoom-val">{zoom.toFixed(2)}x</span>
        </div>

        <div className="avatar-crop-actions">
          <button type="button" className="btn btn-outline btn-sm" onClick={onCancel} disabled={working}>{lang.COMMON_CANCEL || 'Huỷ'}</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={confirm} disabled={!img || working}>
            {working ? 'Đang tải lên...' : 'Xác nhận'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarCropper;
